import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import {
  BrokerNerdsBarkerCoreModelsInventoryEvent,
  BrokerNerdsBarkerCoreModelsInventoryEventMapping,
  BrokerNerdsBarkerCoreModelsMarketplacesGetListingsResponse,
  getApiMarketplacesMarketplaceIdEventsEventIdListings,
  getGetApiMarketplacesMarketplaceIdEventsEventIdListingsQueryKey,
} from '../../api';
import {
  marketListingsDisabledAtom,
  marketplaceListingsFilteredAtom,
  marketplaceListingsQueryAtom,
  overrideEventAtom,
  overrideMarketplaceEventAtom,
  ruleStateAtom,
  selectedEventAtom,
  selectedMarketplaceEventAtom,
  showIgnoredListingsAtom,
  showOutliersAtom,
  showOwnListingsAtom,
} from '../../data/atoms';
import yasml from '@thirtytech/yasml';
import { useLocalStorage } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGlobalState } from '../../data/Global.state';
import { useLocation } from 'react-router-dom';

export { marketListingsLimit } from '../../data/marketListingsLimit';

function MarketListingState({
  overrideEvent,
  overrideMarketplaceEvent,
  disabled,
}: {
  overrideEvent?: BrokerNerdsBarkerCoreModelsInventoryEvent | null;
  overrideMarketplaceEvent?: BrokerNerdsBarkerCoreModelsInventoryEventMapping | null;
  disabled?: boolean;
}) {
  const globalSelectedMarketplaceEvent = useAtomValue(selectedMarketplaceEventAtom);
  const globalSelectedEvent = useAtomValue(selectedEventAtom);
  const { isMobile } = useGlobalState('isMobile');
  const location = useLocation();
  const isSeasonView = location.pathname === '/seasons';

  // TODO: Need to fix this typing mismatch
  const selectedMarketplaceEvent = overrideMarketplaceEvent !== undefined ? overrideMarketplaceEvent : globalSelectedMarketplaceEvent;
  const selectedEvent = useMemo(() => (overrideEvent !== undefined ? overrideEvent : globalSelectedEvent), [globalSelectedEvent, overrideEvent]);

  const queryClient = useQueryClient();
  const [showOwnListings, setShowOwnListings] = useLocalStorage({
    key: 'setting-showOwnListings',
    defaultValue: true,
    getInitialValueInEffect: false,
  });
  const { isAutoPriced } = useAtomValue(ruleStateAtom);
  const [showIgnoredListings, setShowIgnoredListings] = useLocalStorage({
    defaultValue: false,
    key: 'setting-showIgnoredListings',
    getInitialValueInEffect: false,
  });
  const [showListingAge, setShowListingAge] = useLocalStorage({
    defaultValue: false,
    key: 'setting-showListingAge',
    getInitialValueInEffect: false,
  });
  const [showOutliers, setShowOutliers] = useLocalStorage({
    defaultValue: false,
    key: 'setting-showOutliers',
    getInitialValueInEffect: true,
  });
  const setShowOwnListingsOnAtom = useSetAtom(showOwnListingsAtom);
  const setShowIgnoredListingsOnAtom = useSetAtom(showIgnoredListingsAtom);
  const setShowOutliersOnAtom = useSetAtom(showOutliersAtom);

  useEffect(() => {
    setShowOwnListingsOnAtom(overrideEvent || isMobile ? true : showOwnListings);
    setShowIgnoredListingsOnAtom(overrideEvent || isMobile ? true : showIgnoredListings);
    setShowOutliersOnAtom(overrideEvent || isMobile ? true : showOutliers);
  }, [overrideEvent, setShowIgnoredListingsOnAtom, setShowOutliersOnAtom, setShowOwnListingsOnAtom, showIgnoredListings, showOutliers, showOwnListings, isMobile]);

  // The main listings fetch now lives in marketplaceListingsQueryAtom (shared app
  // QueryClient, fully derived). `data` is the full Axios response — the same shape
  // this hook consumed before — so downstream code is unchanged.
  const listingsQuery = useAtomValue(marketplaceListingsQueryAtom);
  const marketplaceListingResult = listingsQuery.data;
  const isFetching = listingsQuery.isFetching;

  // Mirror the fetch-driving props into atoms so marketplaceListingsQueryAtom can
  // derive the listings (and the dataset key) — no imperative push, no rAF. These
  // atoms are scoped per season card; globally they stay at their no-override
  // defaults. A one-render lag vs the props is acceptable: on /seasons the staleness
  // refetch is already gated by isSeasonView, and React Query dedupes any transient
  // key to a cache hit. `undefined` => no override (use global); `null` => no event.
  const setOverrideMarketplaceEvent = useSetAtom(overrideMarketplaceEventAtom);
  const setOverrideEvent = useSetAtom(overrideEventAtom);
  const setMarketListingsDisabled = useSetAtom(marketListingsDisabledAtom);
  useEffect(() => {
    setOverrideMarketplaceEvent(
      overrideMarketplaceEvent === undefined
        ? undefined
        : overrideMarketplaceEvent === null
          ? null
          : { marketplaceId: overrideMarketplaceEvent.marketplaceId, marketplaceEventId: overrideMarketplaceEvent.marketplaceEventId },
    );
    setOverrideEvent(overrideEvent);
    setMarketListingsDisabled(!!disabled);
  }, [overrideMarketplaceEvent, overrideEvent, disabled, setOverrideMarketplaceEvent, setOverrideEvent, setMarketListingsDisabled]);

  const { refetch: fetchUpdatedListings, isFetching: isFetchingUpdatedListings } = useQuery({
    queryKey: [selectedMarketplaceEvent?.marketplaceId, selectedMarketplaceEvent?.marketplaceEventId, selectedEvent?.tenantId],
    queryFn: () => {
      if (!selectedEvent?.tenantId || !selectedMarketplaceEvent?.marketplaceId || !selectedMarketplaceEvent?.marketplaceEventId) {
        return Promise.resolve({ data: {} as BrokerNerdsBarkerCoreModelsMarketplacesGetListingsResponse });
      }
      return getApiMarketplacesMarketplaceIdEventsEventIdListings(
        selectedMarketplaceEvent?.marketplaceId!,
        selectedMarketplaceEvent?.marketplaceEventId!,
        {
          bypassCache: true,
        },
        {
          headers: {
            'x-tenant-id': selectedEvent?.tenantId,
          },
        },
      ).then((data) => {
        queryClient.setQueryData(
          [
            ...getGetApiMarketplacesMarketplaceIdEventsEventIdListingsQueryKey(selectedMarketplaceEvent?.marketplaceId!, selectedMarketplaceEvent?.marketplaceEventId!, {
              bypassCache: false,
            }),
            selectedEvent?.tenantId,
          ],
          data,
        );
        return data;
      });
    },
    enabled: false,
  });

  useEffect(() => {
    if (!marketplaceListingResult) {
      return;
    }

    //@ts-ignore - Header date is not on header object
    if (marketplaceListingResult.data.asOfDate >= dayjs(marketplaceListingResult.headers.get('date')).subtract(5, 'minutes').toDate()) {
      return;
    }

    if (disabled || isSeasonView) {
      return;
    }

    if (selectedMarketplaceEvent?.marketplaceId && selectedMarketplaceEvent?.marketplaceEventId) {
      fetchUpdatedListings();
    }
  }, [disabled, fetchUpdatedListings, isSeasonView, marketplaceListingResult, selectedMarketplaceEvent?.marketplaceEventId, selectedMarketplaceEvent?.marketplaceId]);

  const rowDataRaw = useMemo(() => marketplaceListingResult?.data.listings || [], [marketplaceListingResult]);
  const rowData = useAtomValue(marketplaceListingsFilteredAtom);

  const summaryTotals = useMemo(
    () => ({
      totalListings: rowDataRaw.length,
      totalFilteredListings: rowData?.length || 0,
      totalTickets: rowDataRaw.reduce((acc, listing) => acc + (listing.quantity || 0), 0),
      totalFilteredTickets: rowData?.reduce((acc, listing) => acc + (listing.quantity || 0), 0) || 0,
    }),
    [rowData, rowDataRaw],
  );

  return {
    rowData,
    summaryTotals,
    isFetching,
    setShowOwnListings,
    showOwnListings,
    isLoading: isFetching,
    showIgnoredListings,
    setShowIgnoredListings,
    isAutoPriced,
    isFetchingUpdatedListings,
    fetchUpdatedListings,
    showListingAge,
    setShowListingAge,
    showOutliers,
    setShowOutliers,
    unfilteredListings: rowDataRaw,
    marketplaceId: selectedMarketplaceEvent?.marketplaceId,
    selectedMarketplaceEvent,
  };
}

export const { Provider: MarketListingProvider, useSelector: useMarketListings } = yasml(MarketListingState);
